import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Re-renders the cart drawer section from the server.
   * Uses a cache-busting URL parameter to bypass the browser HTTP cache,
   * which can serve stale section HTML on back/forward navigations.
   */
  #refreshDrawerSection = () => {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset.sectionId;

    if (sectionId) {
      const url = new URL(window.location.href);
      url.searchParams.set('_', Date.now());
      sectionRenderer.renderSection(sectionId, { cache: false, url });
    }
  };

  /**
   * Handles page restoration via bfcache or back/forward HTTP cache.
   * pageshow fires after load, so the PerformanceNavigationTiming entry
   * is guaranteed to exist — unlike in connectedCallback where module
   * scripts with fetchpriority="low" may run before it's available.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) {
      this.#refreshDrawerSection();
      return;
    }
    const navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry?.type === 'back_forward') {
      this.#refreshDrawerSection();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
